/* ═══════════════════════════════════════════════════════════════════════════
   rfhq_header.js  —  runtime for the RFHQ site header

   The header is frappe's own navbar wrapped in an RFHQ <header> by
   templates/rfhq_base.html. This file adds the behaviour that wrapper makes
   possible — scroll states, transparent-over-hero confirmation, an off-canvas
   drawer and dropdown hover-intent — WITHOUT re-rendering any of the markup
   inside. webshop's cart and wishlist, frappe's login state and the language
   switcher all keep working because none of them are touched here.

   ── Division of labour with Bootstrap 4 ─────────────────────────────────────
   Bootstrap 4.6 is present on every website page (frappe-web.bundle) and it
   keeps ownership of dropdowns: frappe's website.js calls $('.dropdown-toggle')
   .dropdown() on every 'page-change'. So this file NEVER binds click on a
   .dropdown-toggle. Hover-intent goes through BS4's own show/hide API instead,
   which keeps open/closed state in exactly one place.

   Collapse is the opposite: we take it over completely. BS4 delegates collapse
   off [data-toggle="collapse"] at the document level, so removing that
   attribute at runtime is a genuine unbind rather than a race — and stripping
   .collapse/.navbar-collapse off the panel means the drawer CSS never has to
   fight .collapse{display:none} or .collapsing{height:0;overflow:hidden}.

   Because that rewrite only happens if this file runs, a load failure leaves
   Bootstrap's collapse fully intact. The fallback is self-healing.

   ── State ───────────────────────────────────────────────────────────────────
   Read from the root (server-stamped by tokens.html):
     data-rfhq-sticky            sticky | shrink | autohide
     data-rfhq-overlay           light | dark        transparent-header ink
     data-rfhq-overlay-solidify  go solid once scrolled past
     data-rfhq-drawer-side       left | right
     data-rfhq-drawer-bp         576 | 768 | 992
     data-rfhq-mega-trigger      click | hover

   Written back by this file, and styled by public/css/rfhq_header.css:
     data-rfhq-scrolled  data-rfhq-hidden  data-rfhq-drawer-open
     data-rfhq-drawer-mobile

   No transitions or durations are set here — they all live in the CSS behind
   @media (prefers-reduced-motion: no-preference), so reduced motion stays a
   purely declarative concern.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var HOVER_CLOSE_MS = 120;

  /* Elements that can hold focus, for the drawer's focus trap. Queried live on
     each Tab rather than cached: webshop injects the mini-cart panel and
     frappe's render_user() swaps the avatar in after load. */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Utility-bar rotation period. Matches the 3s of the hand-rolled Bootstrap
     banner this replaced, which is also about the shortest dwell a short line
     can be read in. */
  var ROTATE_MS = 3000;

  frappe.ready(function () {
    var root = document.querySelector('.rfhq-header');
    if (!root) return;

    var navbar = root.querySelector('.navbar');
    if (!navbar) return;

    var drawer = setupDrawer(root);
    setupScroll(root, drawer);
    setupHover(root);
    setupSearch(root);
    setupUtility(root);
    markActive(navbar);

    window.rfhqHeader = {
      open: function () { drawer && drawer.open(); },
      close: function () { drawer && drawer.close(); },
      isOpen: function () { return root.hasAttribute('data-rfhq-drawer-open'); }
    };
  });

  /* ── Scroll states ──────────────────────────────────────────────────────── */
  /* One passive listener, rAF-throttled, driving every scroll-derived state.
     A transparent header waits until it has been scrolled fully out of view
     before solidifying: it switches from absolute to fixed at that moment, and
     doing it any earlier would show the jump. */
  function setupScroll(root, drawer) {
    var sticky = root.getAttribute('data-rfhq-sticky');
    var overlay = root.hasAttribute('data-rfhq-overlay') &&
      root.hasAttribute('data-rfhq-overlay-solidify');

    if (!sticky && !overlay) return;

    var lastY = window.pageYOffset || 0;
    var frame = null;

    function apply() {
      frame = null;
      var y = window.pageYOffset || 0;
      var threshold = overlay ? root.offsetHeight || 64 : 8;

      if (y > threshold) {
        root.setAttribute('data-rfhq-scrolled', '');
      } else {
        root.removeAttribute('data-rfhq-scrolled');
      }

      /* Auto-hide never hides the header while the drawer is open — the drawer
         is anchored to it, and hiding one without the other strands the user. */
      if (sticky === 'autohide' && !root.hasAttribute('data-rfhq-drawer-open')) {
        if (y > lastY && y > threshold * 2) {
          root.setAttribute('data-rfhq-hidden', '');
        } else if (y < lastY) {
          root.removeAttribute('data-rfhq-hidden');
        }
      }

      lastY = y;
    }

    window.addEventListener('scroll', function () {
      if (frame === null) frame = window.requestAnimationFrame(apply);
    }, { passive: true });

    apply();
  }

  /* ── Off-canvas drawer ──────────────────────────────────────────────────── */
  function setupDrawer(root) {
    var side = root.getAttribute('data-rfhq-drawer-side');
    if (!side) return null;

    var panel = root.querySelector('#navbarSupportedContent');
    var burger = root.querySelector('.navbar-toggler');
    if (!panel || !burger) return null;

    /* The takeover. After this, Bootstrap has neither an attribute to delegate
       from nor a class to style, so the two systems cannot both be live. */
    panel.classList.remove('collapse', 'navbar-collapse', 'show', 'collapsing');
    panel.classList.add('rfhq-header-panel');
    burger.removeAttribute('data-toggle');
    burger.removeAttribute('data-target');
    burger.setAttribute('aria-controls', 'navbarSupportedContent');
    burger.setAttribute('aria-expanded', 'false');

    /* The Minimal variant keeps everything in the drawer at every width, so it
       marks its own nav rather than relying on the breakpoint. Matching a
       media query that is always true is the simplest way to express that
       without a second code path through open/close/syncMode. */
    var always = !!root.querySelector('[data-rfhq-drawer-always]');
    var bp = parseInt(root.getAttribute('data-rfhq-drawer-bp'), 10) || 992;
    var mq = window.matchMedia(always ? '(min-width: 0px)' : '(max-width: ' + (bp - 0.02) + 'px)');

    var head = buildHead(panel);
    var scrim = buildScrim(root);
    var lastFocus = null;

    function isMobile() { return root.hasAttribute('data-rfhq-drawer-mobile'); }

    function syncMode() {
      if (mq.matches) {
        root.setAttribute('data-rfhq-drawer-mobile', '');
      } else {
        /* Leaving drawer territory: close first, so the body scroll lock and
           the inert flags never outlive the presentation that set them. */
        close();
        root.removeAttribute('data-rfhq-drawer-mobile');
      }
      /* The markup's navbar-expand-lg class hides/shows this button off
         Bootstrap's own fixed 992px breakpoint, which only matches `bp` when
         a site leaves the drawer breakpoint at its 992 default. At any other
         `bp` (Website Settings > Header > Drawer Breakpoint) that leaves a
         window — e.g. bp=768 shows the burger from 768-991px even though the
         menu is already rendered inline there, not collapsed. `hidden` wins
         over navbar-expand-lg's CSS regardless of `bp`, so the two can never
         disagree again. */
      burger.hidden = !isMobile();
    }

    function open() {
      if (!isMobile() || root.hasAttribute('data-rfhq-drawer-open')) return;
      lastFocus = document.activeElement;
      root.setAttribute('data-rfhq-drawer-open', '');
      burger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-label', burger.getAttribute('aria-label') || 'Menu');
      setInert(true);
      /* Lock <html>, not <body>: iOS Safari ignores overflow:hidden on body. */
      document.documentElement.style.overflow = 'hidden';

      /* The panel rests at visibility:hidden, and an element that is still
         visibility:hidden when focus() is called silently refuses it — the
         drawer would open with focus stranded on <body>.

         Setting the attribute above only INVALIDATES style; it does not
         recompute it. Reading a layout property forces that recalc to happen
         now, so the panel is genuinely visible by the time focus() runs on the
         next line. requestAnimationFrame is the wrong tool here and was the
         original bug: rAF callbacks run BEFORE style and layout in the frame,
         so the panel is still hidden inside one. */
      void panel.offsetWidth;
      var target = head.querySelector('.rfhq-header-drawer-close');
      if (target) target.focus();
    }

    function close() {
      if (!root.hasAttribute('data-rfhq-drawer-open')) return;
      root.removeAttribute('data-rfhq-drawer-open');
      burger.setAttribute('aria-expanded', 'false');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      panel.removeAttribute('aria-label');
      setInert(false);
      document.documentElement.style.overflow = '';

      /* Focus goes back to the burger rather than to whatever held it before.
         The burger is the control that owns this drawer, which is what the
         disclosure pattern calls for — and `lastFocus` is unreliable anyway:
         a mouse click does not focus a <button> in every browser, so it is
         often <body>, and returning focus there strands the keyboard user at
         the top of the document. lastFocus is kept only as the fallback for
         an open() that did not come from the burger. */
      var target = (burger.offsetParent !== null) ? burger : lastFocus;
      if (target && target.focus) target.focus();
      lastFocus = null;
    }

    /* inert is the correct tool and is widely supported now; aria-hidden is the
       fallback for browsers without it. Never applied to the header itself —
       the drawer lives inside it. */
    function setInert(on) {
      ['main', '.web-footer', 'footer'].forEach(function (sel) {
        Array.prototype.forEach.call(document.querySelectorAll(sel), function (el) {
          if (root.contains(el)) return;
          if (on) {
            el.setAttribute('inert', '');
            el.setAttribute('aria-hidden', 'true');
          } else {
            el.removeAttribute('inert');
            el.removeAttribute('aria-hidden');
          }
        });
      });
    }

    function trap(e) {
      var items = Array.prototype.filter.call(
        panel.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('.rfhq-header .navbar-toggler')) {
        e.preventDefault();
        if (root.hasAttribute('data-rfhq-drawer-open')) close(); else open();
        return;
      }
      if (e.target.closest('.rfhq-header-drawer-close, .rfhq-header-drawer-scrim')) {
        e.preventDefault();
        close();
        return;
      }
      /* Following a link inside the drawer navigates away; leaving the lock in
         place would strand a cached back-navigation with a frozen page. */
      if (root.hasAttribute('data-rfhq-drawer-open') &&
          e.target.closest('.rfhq-header-panel a[href]')) {
        close();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (!root.hasAttribute('data-rfhq-drawer-open')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') trap(e);
    });

    /* addListener is the deprecated spelling, kept as the Safari <14 fallback. */
    if (mq.addEventListener) mq.addEventListener('change', syncMode);
    else if (mq.addListener) mq.addListener(syncMode);
    syncMode();

    return { open: open, close: close };
  }

  /* ── Utility bar · rotating announcements ───────────────────────────────── */
  /* utility.html emits every message with only the first marked active; this
     just advances the mark. Nothing is created or destroyed, so there is no
     reflow and the markup degrades to a static first message with JS off.

     Bails on a single message (nothing to rotate) and under reduced-motion,
     where the CSS shows the whole set as one static run instead — so standing
     down here can never leave the strip blank. */
  function setupUtility(root) {
    var rotator = root.querySelector('.rfhq-header-utility-rotator');
    if (!rotator) return;

    var msgs = rotator.querySelectorAll('.rfhq-header-utility-msg');
    if (msgs.length < 2) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) return;

    var i = 0;
    setInterval(function () {
      /* A hidden tab still fires timers (throttled); skipping the swap keeps
         the cycle from silently advancing while nobody can see it. */
      if (document.hidden) return;
      msgs[i].removeAttribute('data-rfhq-msg-active');
      i = (i + 1) % msgs.length;
      msgs[i].setAttribute('data-rfhq-msg-active', '');
    }, ROTATE_MS);
  }

  function buildHead(panel) {
    var head = document.createElement('div');
    head.className = 'rfhq-header-drawer-head';
    head.innerHTML =
      '<button type="button" class="rfhq-header-drawer-close" aria-label="' +
      __('Close menu') +
      '"><svg class="icon icon-md" aria-hidden="true"><use href="#icon-close"></use></svg></button>';
    panel.insertBefore(head, panel.firstChild);
    return head;
  }

  function buildScrim(root) {
    var scrim = document.createElement('div');
    scrim.className = 'rfhq-header-drawer-scrim';
    root.appendChild(scrim);
    return scrim;
  }

  /* ── Dropdown hover-intent ──────────────────────────────────────────────── */
  /* Opt-in, desktop-only, and routed through Bootstrap's own API so that click,
     keyboard and hover all mutate the same state. A close delay keeps the menu
     from vanishing while the pointer crosses the gap to it. */
  function setupHover(root) {
    if (root.getAttribute('data-rfhq-mega-trigger') !== 'hover') return;
    if (typeof window.jQuery === 'undefined') return;
    if (!window.matchMedia('(hover: hover)').matches) return;

    var $ = window.jQuery;
    var timer = null;

    root.addEventListener('mouseover', function (e) {
      var item = e.target.closest('.rfhq-header .navbar-nav .dropdown');
      if (!item || root.hasAttribute('data-rfhq-drawer-mobile')) return;
      window.clearTimeout(timer);
      var toggle = item.querySelector('.dropdown-toggle');
      if (toggle && !item.classList.contains('show')) $(toggle).dropdown('toggle');
    });

    root.addEventListener('mouseout', function (e) {
      var item = e.target.closest('.rfhq-header .navbar-nav .dropdown');
      if (!item) return;
      /* Ignore moves that stay inside the same dropdown. */
      if (e.relatedTarget && item.contains(e.relatedTarget)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        var toggle = item.querySelector('.dropdown-toggle');
        if (toggle && item.classList.contains('show')) $(toggle).dropdown('toggle');
      }, HOVER_CLOSE_MS);
    });
  }

  /* ── Search toggle ──────────────────────────────────────────────────────── */
  /* Deliberately not Bootstrap's collapse: BS4 animates height and would pull
     the panel back into the .collapsing rules the drawer was freed from. The
     `hidden` attribute carries the state so the field is genuinely out of the
     tab order while closed, rather than merely invisible. */
  function setupSearch(root) {
    var toggle = root.querySelector('[data-rfhq-search-toggle]');
    var panel = root.querySelector('.rfhq-header-search-panel');
    if (!toggle || !panel) return;

    function close() {
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      root.removeAttribute('data-rfhq-search-open');
    }

    toggle.addEventListener('click', function () {
      var opening = panel.hidden;
      panel.hidden = !opening;
      toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
      if (opening) {
        root.setAttribute('data-rfhq-search-open', '');
        var field = panel.querySelector('input[name="q"]');
        if (field) field.focus();
      } else {
        close();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) {
        close();
        toggle.focus();
      }
    });
  }

  /* ── Current page ───────────────────────────────────────────────────────── */
  /* frappe's website.js marks the matching <li> with .active but stops there;
     aria-current is what actually conveys it to assistive tech.

     Bound to 'page-change' rather than run once: website.js calls
     make_navbar_active from its own page-change handler (website.js:641,659),
     and whether that has happened by the time our frappe.ready callback fires
     depends on registration order. Listening for the same event removes the
     ordering question instead of betting on it. */
  function markActive(navbar) {
    function apply() {
      Array.prototype.forEach.call(navbar.querySelectorAll('li.active > a[href]'), function (a) {
        a.setAttribute('aria-current', 'page');
      });
    }
    apply();
    if (typeof window.jQuery !== 'undefined') {
      window.jQuery(document).on('page-change', apply);
    }
  }

  /* frappe's translation helper is not guaranteed to exist this early. */
  function __(text) {
    return (typeof window.__ === 'function' ? window.__(text) : text);
  }
})();
